import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { FormField, form, required, schema, submit } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { catchError, firstValueFrom, tap, throwError } from 'rxjs';
import { ApiException, MceApiService, PublicUrlDto, UserDto, UserUpdateEmailDto, UserUpdatePhotoDto } from '../../core/mceapi.client.generated';
import { getApiErrorMessage } from '../../core/mceapi.client.helpers';
import { MceImageUploadComponent } from '../../shared/components/mce-image-upload/mce-image-upload.component';
import { MceSaveCancelComponent, MceSaveCancelConfig, MceSaveCancelEvent } from '../../shared/components/mce-save-cancel/mce-save-cancel.component';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { unwrap } from '../../shared/functions/functional';
import { PageWithNavigationComponent } from '../../shared/page-templates/page-with-navigation/page-with-navigation.component';
import { PageWithSplashComponent } from '../../shared/page-templates/page-with-splash/page-with-splash.component';
import { AuthenticationService } from '../../authentication/authentication.service';
import { FirebaseEventService } from '../../authentication/firebase.event.service';
import { DebugPanelComponent } from '../../shared/components/debug-panel/debug-panel.component';

interface ProfileEditFormModel {
  uid: string;
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
  photoUrl?: PublicUrlDto;
  imageData64: string;
}

const profileEditFormSchema = schema<ProfileEditFormModel>((path) => {
  required(path.displayName, { message: `Display Name is required` });
  required(path.firstName, { message: `First Name is required` });
  required(path.lastName, { message: `Last Name is required` });
});

@Component({
  selector: 'mce-profile-edit',
  imports: [NgTemplateOutlet, PageWithNavigationComponent, PageWithSplashComponent, FormField, MatFormFieldModule, MatInputModule, MatIconModule,
    MatProgressSpinnerModule, MatToolbarModule, MceSaveCancelComponent,
    MceImageUploadComponent, MatButtonModule, DebugPanelComponent],
  templateUrl: './profile-edit.component.html'
})
export class ProfileEditComponent {

  private authenticationService = inject(AuthenticationService);
  private mceApiService = inject(MceApiService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private urlSerializer = inject(UrlSerializer);
  private dialogService = inject(DialogService);
  private eventService = inject(FirebaseEventService);

  // Keep the entry layout stable while the saved user state refreshes before navigation.
  protected readonly isOnboarding = !this.authenticationService.currentUserProfileComplete();
  private readonly onboardingDestination = this.getOnboardingDestination();

  private readonly currentUser = unwrap(this.authenticationService.currentUser());

  readonly profileEditModel = signal<ProfileEditFormModel>({
    uid: this.currentUser.uid ?? '',
    displayName: this.currentUser.displayName ?? '',
    email: this.currentUser.email ?? '',
    firstName: this.currentUser.firstName ?? '',
    lastName: this.currentUser.lastName ?? '',
    photoUrl: this.currentUser.photoUrl,
    imageData64: ''
  });
  readonly profileEditForm = form<ProfileEditFormModel>(this.profileEditModel, profileEditFormSchema);

  protected readonly saveCancelConfig = computed<MceSaveCancelConfig>(() => ({
    save: { show: !this.isOnboarding, enable: this.profileEditForm().valid() && this.profileEditForm().dirty() && !this.profileEditForm().submitting() },
    cancel: { show: !this.isOnboarding, enable: !this.profileEditForm().submitting() }
  }));

  updatePhotoDto = computed<UserUpdatePhotoDto>(() => {
    return {
      userUid: this.profileEditForm().value().uid,
      photo64Data: this.profileEditForm().value().imageData64
    };
  });

  updateUserDto = linkedSignal<UserDto>(() => {
    return {
      uid: this.profileEditForm().value().uid,
      displayName: this.profileEditForm().value().displayName,
      firstName: this.profileEditForm().value().firstName,
      lastName: this.profileEditForm().value().lastName,
      photoUrl: this.profileEditForm().value().photoUrl
    }
  });

  private getOnboardingDestination(): string {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (!returnUrl?.startsWith('/') || returnUrl.startsWith('//') || returnUrl.includes('\\')) {
      return '/dashboard';
    }

    try {
      const tree = this.urlSerializer.parse(returnUrl);
      const segments = tree.root.children['primary']?.segments.map(segment => segment.path) ?? [];
      if (segments[0] === 'authentication' || (segments[0] === 'profile' && segments[1] === 'edit')) {
        return '/dashboard';
      }
      return returnUrl;
    } catch {
      return '/dashboard';
    }
  }

  protected onSave() {

    submit(this.profileEditForm, async (form) => {

      try {

        if (this.profileEditForm.imageData64().dirty()) {
          await firstValueFrom(
            this.mceApiService.v1UsersProfilePhotoUpdate(this.updatePhotoDto()).pipe(
              tap(url => this.updateUserDto.update(user => ({...user, photoUrl:url }))),
              catchError(error => this.handleUpdateProfilePhotoError(error))
            ));
        }

        await firstValueFrom(this.mceApiService.v1UsersUpdate(this.updateUserDto()));
        await firstValueFrom(this.authenticationService.refreshCurrentUser());
        if (this.isOnboarding) {
          if (!this.authenticationService.currentUserProfileComplete()) {
            return [{ kind: 'unexpected', message: 'Your profile is still incomplete. Please check your details and try again.' }];
          }
          await this.router.navigateByUrl(this.onboardingDestination);
        } else {
          await this.router.navigate(['/account']);
        }

        return undefined;
      }
      
      catch (error) {
        const message = getApiErrorMessage(error);
        console.log('Edit profile error', message);

        return [{
          message,
          kind: 'unexpected'
        }];
      } });
  }

  private handleUpdateProfilePhotoError(error: unknown) {
    if (ApiException.isApiException(error) && error.status === 400) {
      this.dialogService.acknowledge(
        'Unable to update profile photo',
        getApiErrorMessage(error),
        '320px'
      ).subscribe();
    }

    return throwError(() => error);
  }

  protected onCancel() {
    if (!this.isOnboarding && !this.profileEditForm().submitting()) {
      this.router.navigate(['/account']);
    }
  }

  protected onSaveCancelAction(event: MceSaveCancelEvent): void {
    if (event.type === 'save') {
      this.onSave();
      return;
    }

    if (event.type === 'cancel') {
      this.onCancel();
    }
  }

  onSignOut() {
    this.authenticationService.signOut().pipe(
      tap(() => this.router.navigate(['/authentication/signin']))
    ).subscribe();
  }

  async onChangeEmail() {
    const result = await this.dialogService.changeEmail();
    if (!result) return;

    const userUid = this.authenticationService.user()?.uid;
    if (!userUid) return;

    try {
      const updateEmailDto: UserUpdateEmailDto = {
        ...result,
        userUid
      };

      await firstValueFrom(this.mceApiService.v1UsersEmailUpdate(updateEmailDto));
      this.eventService.logChangeEmailEvent(this.authenticationService.currentUser());
    }
    catch (error) {
      console.log('Change email error', error);
      await firstValueFrom(
        this.dialogService.acknowledge(
          'Unable to change email',
          (error as Error).message,
          '320px'
        )
      );
      return;
    }

    await firstValueFrom(
      this.dialogService.acknowledge(
        'Email changed',
        'Your email address has been updated. You can now log in with your new email address.',
        '320px'
      )
    );

    try {
      await firstValueFrom(this.authenticationService.signOut());
    }
    catch (error) {
      console.log('Sign out after change email error', error);
    }

    this.router.navigate(['/authentication/signin']);
  }

  async onDeleteAccount() {

    const result = await this.dialogService.unregisterUser();
    if (!result) return;

    const userUid = this.authenticationService.user()?.uid;
    if (!userUid) return;

    this.authenticationService.blockRefresh(true);

    try {
      await firstValueFrom(this.mceApiService.v1UsersUnregister({
        userUid,
        otpToken: result.otpToken,
        otp: result.otp
      }));

      this.eventService.logUnregisterUserEvent(this.authenticationService.currentUser());
    }
    catch (error) {
      console.log('Delete account error', error);
      return;
    }
    finally {
      this.authenticationService.blockRefresh(false);
    }

    try {
      await firstValueFrom(this.authenticationService.signOut());
    }
    catch (error) {
      console.log('Sign out after delete account error', error);
    }

    this.authenticationService.blockRefresh(false);
    this.router.navigate(['/authentication/signin']);
  }

}
